import React from 'react'

import { Typography } from '@material-ui/core'
import { AbstractCollegeResultTile, AbstractCollegeSearch, CollegeSearchProvider, mapData } from '@scoir/college-search'
import qs from '@scoir/query-string'
import { constant, get, isUndefined, map, noop } from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { UrlQueryParamTypes, addUrlProps } from 'react-url-query'

import api from 'common/api'
import tagsService from 'common/tags-service'
import CollegeTypeahead from 'components/CollegeTypeahead'
import { isTnTSearchFilterEnabled } from 'store/feature-toggle'
import { actions as savedSearchActions } from 'store/saved-search'

import './index.scss'

const PAGE_SIZE = 25
const MAX_MAJORS = 20
const MAJORS_PAGE_SIZE = MAX_MAJORS + 1
const getImmutableQuery = (query) => (isUndefined(query) ? undefined : { ...query })
const getCollegeDetailsUrl = (scid) => `/admissions/colleges/${scid}`

const getQuery = (
  publicPrivate,
  setting,
  religious,
  minorityServingInst,
  prepopulatedNameSearch,
  persistedQuery,
  predefinedFilters,
) => {
  if (!isUndefined(publicPrivate)) {
    return {
      Control: [publicPrivate],
    }
  }
  if (!isUndefined(setting)) {
    return {
      Setting: [setting],
    }
  }
  if (!isUndefined(religious)) {
    return {
      Religious: [religious],
    }
  }
  if (!isUndefined(minorityServingInst)) {
    return {
      MinorityServingInst: [minorityServingInst],
    }
  }
  if (!isUndefined(predefinedFilters)) {
    if (predefinedFilters === 'APPLY_WITH_SCOIR') {
      return {
        ScoirApplyApplicantTypes: ['first', 'transfer'],
      }
    }
    if (predefinedFilters === 'COALITION_MEMBER') {
      return {
        CoalitionMember: ['T'],
      }
    }
  }
  return prepopulatedNameSearch ? {} : getImmutableQuery(persistedQuery)
}

export class CollegeSearchRoute extends React.Component {
  constructor(props) {
    super(props)
    const {
      persistedQuery,
      persistedNameSearch,
      prepopulatedNameSearch,
      publicPrivate,
      setting,
      religious,
      minorityServingInst,
      predefinedFilters,
    } = props
    const query = getQuery(
      publicPrivate,
      setting,
      religious,
      minorityServingInst,
      prepopulatedNameSearch,
      persistedQuery,
      predefinedFilters,
    )
    const nameSearch = isUndefined(query) ? prepopulatedNameSearch || persistedNameSearch : undefined
    this.state = {
      academicFocusSearchTerm: '',
      query,
      nameSearch,
      page: 0,
      total: 0,
      facets: [],
      colleges: [],
      isLoadingNextPage: false,
    }
  }
  static propTypes = {
    history: PropTypes.object,
    persistedQuery: PropTypes.object,
    persistedNameSearch: PropTypes.string,
    prepopulatedNameSearch: PropTypes.string,
    updateSavedSearch: PropTypes.func.isRequired,
    publicPrivate: PropTypes.string,
    setting: PropTypes.string,
    religious: PropTypes.string,
    minorityServingInst: PropTypes.string,
    predefinedFilters: PropTypes.string,
    isTnTSearchFilterEnabled: PropTypes.bool,
  }
  static defaultProps = {
    persistedNameSearch: '',
  }
  componentDidMount() {
    document.title = 'Scoir - College Search'
  }
  loadSearch(query, page, nameSearch = this.state.nameSearch) {
    const pageFrom = PAGE_SIZE * page
    const sort = !isUndefined(get(query, 'Location')) ? 'distance' : ''
    const params = qs.stringify({
      pageSize: PAGE_SIZE,
      sort,
      name: nameSearch,
      from: pageFrom,
    })
    return api
      .post(`/api/university/search?${params}`, query)
      .then((searchResponse) => {
        const colleges = mapData(searchResponse)
        this.setState({
          page,
          query,
          nameSearch,
          total: searchResponse.Total,
          facets: searchResponse.Facets,
        })
        this.props.updateSavedSearch(query, nameSearch)
        return colleges
      })
      .catch(noop)
  }
  nextPage() {
    if (!this.state.isLoadingNextPage) {
      const page = this.state.page + 1
      this.setState({ isLoadingNextPage: true })
      return this.loadSearch(this.state.query, page)
        .then((colleges) => {
          const updatedColleges = [...this.state.colleges, ...colleges]
          this.setState({
            colleges: updatedColleges,
            isLoadingNextPage: false,
          })
        })
        .catch(noop)
    }
  }
  onQueryChange(query, nameSearch = this.state.nameSearch) {
    return this.loadSearch(query, 0, nameSearch)
      .then((colleges = []) => {
        this.setState({ colleges })
      })
      .catch(noop)
  }
  dismissNameSearchFilter(query = this.state.query) {
    this.onQueryChange(query, '')
  }
  loadPersonalInterests(input) {
    return tagsService.fetchMediaByTerm(input).catch(constant([]))
  }
  loadLocations(input) {
    return api.get(`/api/locations/search/${input}`).catch(constant([]))
  }
  loadSports(input) {
    return api.get(`/api/sport/search/${input}?limit=8`).catch(constant([]))
  }
  academicSearch(term, page) {
    this.setState({ academicFocusSearchTerm: term })
    const majorsFrom = MAJORS_PAGE_SIZE * page
    return api
      .get(
        `/api/academicfocus/majors?search=${encodeURIComponent(
          term,
        )}&majorsFrom=${majorsFrom}&majorsPageSize=${MAJORS_PAGE_SIZE}`,
      )
      .then((results) => ({
        Focus: map(results.Keywords, (result) => ({
          name: result.Keyword,
        })),
        Majors: map(results.Majors, (result) => ({
          name: result.CipName,
          type: result.Type,
        })),
      }))
  }
  renderTopOfRail() {
    const onSelect = (result) => {
      const { Scid, SchoolName: nameSearch, customOption } = result
      if (customOption) {
        this.onQueryChange({}, nameSearch)
      } else {
        this.props.history.push(getCollegeDetailsUrl(Scid))
      }
    }
    return (
      <div className="cs-college-name-search">
        <h3>College Name</h3>
        <CollegeTypeahead
          allowNew={true}
          clearOnSelect={true}
          onChange={(result) => onSelect(result)}
        />
      </div>
    )
  }
  renderResultTile(college, isTnTSearchFilterEnabled) {
    return (
      <AbstractCollegeResultTile
        key={college.Scid}
        college={college}
        detailLink={getCollegeDetailsUrl(college.Scid)}
        isTnTSearchFilterEnabled={isTnTSearchFilterEnabled}
        noContent
      />
    )
  }
  render() {
    const { query, total, facets, colleges, nameSearch } = this.state
    return (
      <div className="counselor-college-search">
        <Typography variant="srOnly">College Search</Typography>
        <CollegeSearchProvider
          onAcademicFocusSearch={(term) => this.academicSearch(term, 0)}
          onPageMajors={(page) => this.academicSearch(this.state.academicFocusSearchTerm, page)}
          majorsMaxResults={MAX_MAJORS}
        >
          <AbstractCollegeSearch
            colleges={colleges}
            total={total}
            query={query}
            facets={facets}
            nameSearch={nameSearch}
            renderResultTile={(college) => this.renderResultTile(college, this.props.isTnTSearchFilterEnabled)}
            topOfRailComponent={this.renderTopOfRail()}
            onPageChange={() => this.nextPage()}
            onQueryChange={(query) => this.onQueryChange(query)}
            onNameSearchDismiss={() => this.dismissNameSearchFilter()}
            onResetSearch={(query) => this.dismissNameSearchFilter(query)}
            onPersonalInterestSearch={(input) => this.loadPersonalInterests(input)}
            onSportsSearch={(input) => this.loadSports(input)}
            onLocationSearch={(input) => this.loadLocations(input)}
            onGetAllInterests={tagsService.getAllTags}
          />
        </CollegeSearchProvider>
      </div>
    )
  }
}

export const ConnectedCollegeSearchRoute = connect(
  (state) => ({
    persistedQuery: get(state, 'savedSearch.query'),
    persistedNameSearch: get(state, 'savedSearch.nameSearch'),
    persistedSearchUserId: get(state, 'savedSearch.userSearchId'),
    isTnTSearchFilterEnabled: isTnTSearchFilterEnabled(state),
  }),
  {
    updateSavedSearch: savedSearchActions.updateSavedSearch,
  },
)(CollegeSearchRoute)

const urlPropsQueryConfig = {
  publicPrivate: {
    type: UrlQueryParamTypes.string,
    queryParam: 'publicPrivate',
  },
  setting: { type: UrlQueryParamTypes.string, queryParam: 'setting' },
  religious: { type: UrlQueryParamTypes.string, queryParam: 'religious' },
  minorityServingInst: {
    type: UrlQueryParamTypes.string,
    queryParam: 'minorityServingInst',
  },
  predefinedFilters: {
    type: UrlQueryParamTypes.string,
    queryParam: 'predefinedFilters',
  },
  selectivityLevel: {
    type: UrlQueryParamTypes.number,
    queryParam: 'selectivityLevel',
  },
}

export const ConnectedCollegeSearchRouteWithUrlProps = addUrlProps({
  urlPropsQueryConfig,
  addUrlChangeHandlers: true,
})(ConnectedCollegeSearchRoute)

export default withRouter(ConnectedCollegeSearchRouteWithUrlProps)
